import { Box } from '@mui/material';
import {
  Button,
  ButtonProps,
  CloseIcon,
  IconButton,
  Typography,
} from '~/index.ts';
import {
  LoadingButton,
  LoadingButtonProps,
} from '~/components/private/index.ts';
import { OverridableStyles } from '~/utils/types.ts';

export interface ActionHeaderProps extends OverridableStyles {
  onClose?: () => void;
  submitForm?: () => void;
  titleText: string;
  /**
   * @deprecated use `actionButtonProps` instead
   */
  actionButtonText?: string;
  actionButtonProps?: LoadingButtonProps;
  cancelButtonVariant?: ButtonProps['variant'];
  cancelButtonText?: string;
  isLoading?: boolean;
  /**
   * @description Disables all controls in the header
   */
  disabled?: boolean;
  dataTestId?: string;
}

const ActionHeader: React.FC<ActionHeaderProps> = ({
  onClose,
  submitForm,
  titleText,
  actionButtonProps,
  cancelButtonText,
  actionButtonText,
  cancelButtonVariant,
  isLoading,
  disabled,
  dataTestId,
  styleOverrides,
}) => {
  const actionButtonContent = actionButtonProps?.children || actionButtonText;
  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '24px',
        borderBottom: '1px solid var(--commander-colorDivider)',
        backgroundColor: 'background.paper',
        // Sometimes ActionHeader will be in containers with rounded borders.
        // See Popover or DrawerContent stories for examples.
        borderTopLeftRadius: 'inherit',
        borderTopRightRadius: 'inherit',
        ...styleOverrides,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <IconButton
          dataTestId={`${dataTestId}-close-button`}
          aria-label="Close"
          size="medium"
          color="inherit"
          onClick={onClose}
          IconComponent={CloseIcon}
          disabled={disabled}
        />
        <Typography variant="h4" styleOverrides={{ margin: 'auto' }}>
          {titleText}
        </Typography>
      </Box>
      <Box>
        {cancelButtonText && (
          <Button
            styleOverrides={{ marginRight: '16px' }}
            variant={cancelButtonVariant}
            size="medium"
            color="default"
            onClick={onClose}
            isDisabled={disabled}
            data-test-id={`${dataTestId}-cancel-button`}
          >
            {cancelButtonText}
          </Button>
        )}
        {actionButtonContent && (
          <LoadingButton
            loading={isLoading}
            variant="contained"
            size="medium"
            type="submit"
            onClick={submitForm}
            data-test-id={`${dataTestId}-action-button`}
            {...actionButtonProps}
            disabled={actionButtonProps?.disabled || disabled}
          >
            {actionButtonContent}
          </LoadingButton>
        )}
      </Box>
    </Box>
  );
};
export default ActionHeader;
